import React, { FC, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import { ConfirmationModal } from "@components/common";
import { useGetCsobDatasetComparison, useUpdateUserModalViewedVersion } from "@api";
import { Stack, Typography, IconButton } from "@components/base";
import { CsobRegimeType, useUserStore } from "@store/user";

const Modal: FC = () => {
  const { user, jwtTokenData } = useUserStore();
  const { updateUserModalViewedVersion, updateUserModalViewedVersionError } =
    useUpdateUserModalViewedVersion(user);
  const {
    csobDatasetComparison,
    csobDatasetComparisonEnabled,
    isPendingGetCsobDatasetComparison,
  } = useGetCsobDatasetComparison(
    user,
    jwtTokenData?.csobRegimeType === CsobRegimeType.Closed,
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const termsAndConditionsAccepted = user?.termsAndConditionsAccepted ?? false;

  const [modalOpen, setModalOpen] = useState(false);

  const handleConfirmModal = (): void => {
    updateUserModalViewedVersion({
      modalDatasetViewedVersion: 1,
    });

    navigate("/csob/dataset-comparison");
    setModalOpen(false);
  };

  const handleCloseModal = (): void => {
    updateUserModalViewedVersion({
      modalDatasetViewedVersion: 1,
    });
    setModalOpen(false);
    enqueueSnackbar(t("csob.hub.modal.notification"), {
      action: (snackbarId) => (
        <IconButton size="small" onClick={() => closeSnackbar(snackbarId)}>
          <FeatherIcon icon="x" size={20} />
        </IconButton>
      ),
      variant: "success",
      autoHideDuration: 6000,
    });
  };

  useEffect(() => {
    if (
      !isPendingGetCsobDatasetComparison &&
      csobDatasetComparison &&
      csobDatasetComparison.displayDatasetModal &&
      termsAndConditionsAccepted
    ) {
      setModalOpen(true);
    }
  }, [termsAndConditionsAccepted, csobDatasetComparison]);

  useEffect(() => {
    if (updateUserModalViewedVersionError) {
      enqueueSnackbar(t(updateUserModalViewedVersionError.message), {
        variant: "error",
        autoHideDuration: 6000,
      });
      setModalOpen(false);
    }
  }, [updateUserModalViewedVersionError, enqueueSnackbar]);

  return (
    csobDatasetComparisonEnabled &&
    csobDatasetComparison && (
      <ConfirmationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmModal}
        title={
          <Stack spacing={2} alignItems="center">
            <FeatherIcon icon="alert-triangle" size={32} />
            <Typography component="div" variant="body1">
              <Trans
                i18nKey="csob.hub.modal.description"
                components={{
                  strong: <Typography component="span" fontWeight="bold" />,
                }}
              />
            </Typography>
          </Stack>
        }
        cancelText={t("csob.hub.modal.button.cancel")}
        confirmText={t("csob.hub.modal.button.confirm")}
      />
    )
  );
};

export default Modal;
